import { api } from './api';

const TOKEN_KEY = "token";

/** Retourne le token depuis sessionStorage (session) ou localStorage (se souvenir de moi). */
function getStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
}

/** Stocke le token dans sessionStorage ou localStorage et nettoie l'autre. */
function setStoredToken(token: string, rememberMe: boolean): void {
  if (rememberMe) {
    localStorage.setItem(TOKEN_KEY, token);
    sessionStorage.removeItem(TOKEN_KEY);
  } else {
    sessionStorage.setItem(TOKEN_KEY, token);
    localStorage.removeItem(TOKEN_KEY);
  }
}

/** Supprime le token des deux storages. */
function clearStoredToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
}

export interface LoginCredentials {
  email: string;
  password: string;
  /** Si true, garde la session après fermeture du navigateur (localStorage). Sinon sessionStorage. */
  rememberMe?: boolean;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: string;
}

/**
 * Payload d'inscription d'un client final (portail client `/client`).
 * Aucun champ `role` : la cr\u00e9ation cr\u00e9e syst\u00e9matiquement un compte CUSTOMER.
 */
export interface RegisterClientData {
  email: string;
  password: string;
  firstName?: string;
  lastName: string;
  phoneDialCode?: string;
  phone?: string;
  customerType?: "INDIVIDUAL" | "COMPANY" | "INSTITUTION";
  termsAccepted: true;
}

export interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Présent quand l'utilisateur est rattaché à une entreprise (API peut renvoyer seulement celui-ci) */
  enterpriseId?: string | null;
  enterprise?: {
    id: string;
    name: string;
    address: string | null;
    postalCode: string | null;
    city: string | null;
    vatRate: number | null;
  } | null;
  /** ID du livreur quand role === DRIVER (pour filtrer livraisons / estimations) */
  driverId?: string | null;
  /** ID de la fiche client quand role === CUSTOMER (portail client). */
  customerId?: string | null;
}

export interface AuthResponse {
  user: User;
  token: string;
}

/**
 * Service d'authentification pour gérer login, register, logout
 */
export const authService = {
  /**
   * Connexion d'un utilisateur
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<{ success: boolean; data: AuthResponse }>(
      "/auth/login",
      { email: credentials.email, password: credentials.password },
    );
    const authData = response.data.data;
    const rememberMe = credentials.rememberMe ?? true;
    authService.setToken(authData.token, rememberMe);
    return authData;
  },

  /**
   * Inscription d'un nouvel utilisateur (token persisté en localStorage par défaut)
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post<{ success: boolean; data: AuthResponse }>(
      '/auth/register',
      data,
    );
    const authData = response.data.data;
    authService.setToken(authData.token, true);
    return authData;
  },

  /**
   * Inscription d'un client final (portail client `/client`).
   * Cr\u00e9e une fiche `Customer` et un `User` avec `role = CUSTOMER`.
   */
  async registerClient(data: RegisterClientData): Promise<AuthResponse> {
    const response = await api.post<{ success: boolean; data: AuthResponse }>(
      "/auth/register-client",
      data,
    );
    const authData = response.data.data;
    authService.setToken(authData.token, true);
    return authData;
  },

  /**
   * Récupère les informations de l'utilisateur connecté
   */
  async getMe(): Promise<User> {
    const response = await api.get<{ success: boolean; data: User }>('/auth/me');
    return response.data.data;
  },

  /**
   * Rattache le compte connecté à l'entreprise Défi Gourmand (Siège).
   * Retourne l'utilisateur mis à jour avec enterprise.
   */
  async linkMyEnterprise(): Promise<User> {
    const response = await api.post<{ success: boolean; data: User }>(
      '/auth/link-my-enterprise',
    );
    return response.data.data;
  },

  /**
   * Déconnexion
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Même en cas d'erreur, on supprime le token côté client
      console.error('Logout error:', error);
    } finally {
      clearStoredToken();
      delete api.defaults.headers.common['Authorization'];
    }
  },

  /**
   * Stocke le token (sessionStorage ou localStorage selon rememberMe) et configure axios
   */
  setToken(token: string, rememberMe = true): void {
    setStoredToken(token, rememberMe);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  },

  /**
   * Récupère le token (sessionStorage prioritaire, puis localStorage)
   */
  getToken(): string | null {
    return getStoredToken();
  },

  /** Supprime le token des deux storages (exporté pour le store). */
  clearStoredToken,

  /**
   * Vérifie si un token est présent
   */
  isAuthenticated(): boolean {
    const token = authService.getToken();
    if (!token) return false;

    // Vérifier que le token n'est pas expiré (basique)
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const exp = payload.exp * 1000; // Convertir en millisecondes
      return Date.now() < exp;
    } catch {
      return false;
    }
  },
};

// Initialiser axios avec le token au chargement si présent
const savedToken = getStoredToken();
if (savedToken) {
  api.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;
}
